// MobileImageMagnifier component
// * This component provides a zoom-in effect for mobile users when they hover or move over the image.
// * It enhances the user experience by offering a closer look at image details without the need for pinch-to-zoom.
// * The magnifier is triggered on hover, showing a magnified portion of the image inside the same container.

/**
 * Workflow:
 * The MobileImageMagnifier accepts `src` (the image to be magnified). As the user moves their finger or mouse over the image,
 * the mouse position is tracked, and the background shifts to display the zoomed-in area of the image.
 * This provides a seamless zooming experience without any additional user interaction like pinch-to-zoom.
 * Dynamic styling is applied via inline styles for the zoom behavior, while static styling is managed using Tailwind CSS.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  mobile,
  mobile_large,
  tablet_small,
  tablet_large,
  desktop,
  desktop_xl,
} from 'helpers/utils/preferredScreenSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';

interface MagnifierProps {
  src: string | undefined;
}

const MobileImageMagnifier = ({ src }: MagnifierProps) => {
  const [magnifierVisible, setMagnifierVisible] = useState(false);
  const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');
  const [ismobile] = useMediaQuery(mobile);
  const [isMobileLarge] = useMediaQuery(mobile_large);
  const [isTabSmall] = useMediaQuery(tablet_small);
  const [isTabLarge] = useMediaQuery(tablet_large);
  const [isdesktop] = useMediaQuery(desktop);
  const [isDesktopXL] = useMediaQuery(desktop_xl);
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);

  // FIX ME : Need to work on the Amplience URL Mapping

  // const srcAmplience =
  //   'https://cdn.media.amplience.net/i/americangolf/267990-Navy-FootJoy-Mens-Stretch-Pique-Solid-Colour-Golf-Polo-Shirt-1';

  // // Memoize the getImageWidth function
  // const imageWidth = useMemo(() => {
  //   if (ismobile) return 343;
  //   if (isMobileLarge) return 735;
  //   if (isTabSmall) return 472;
  //   if (isTabLarge) return 634;
  //   if (isdesktop) return 740;
  //   if (isDesktopXL) return 740;
  //   return 343;
  // }, [ismobile, isMobileLarge, isTabSmall, isTabLarge, isdesktop, isDesktopXL]);

  // // Update the image source whenever the screen size changes
  // useEffect(() => {
  //   if (srcAmplience) {
  //     setImageSrc(`${srcAmplience}?w=${imageWidth}&fmt=webp`);
  //   }
  // }, [srcAmplience, imageWidth]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { top, left, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setBackgroundPosition(`${x}% ${y}%`);
  };

  return (
    <div
      className={`relative  flex justify-center`}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setMagnifierVisible(true)}
      onMouseLeave={() => setMagnifierVisible(false)}
    >
      <img src={src} alt="Magnifiable" />
      {magnifierVisible && (
        <div
          className="pointer-events-none absolute inset-0 bg-no-repeat"
          style={{
            backgroundImage: `url(${src})`,
            backgroundPosition: backgroundPosition,
            backgroundSize: '200% 200%',
            transform: 'scale(1)',
            transformOrigin: 'center center',
          }}
        ></div>
      )}
    </div>
  );
};

export default MobileImageMagnifier;
